import DataTable from "../../../shared/dashboards/DataTable/DataTable";
import {
  EyeOutlined,
  CreditCardOutlined,
  CheckCircleFilled,
  CloseCircleFilled,
} from "@ant-design/icons";

import styles from "./stylesProcessedRequestsTable.module.css";

import CustomPagination from "../../../shared/CustomPagination/CustomPagination";
import { useWithdrawalReview } from "../WithdrawalReviewContext";
import {
  convertToUserTimezone,
  formatTimestampOrDisplay,
} from "../../../../utils/dateService";
import UserAvatar from "../../../CommonComponents/UserAvatar/UserAvatar";
import { formatCurrency } from "../../../../utils/financialFormatters";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import {
  WalletBalanceCell,
  WalletColumnTitle,
} from "../../../shared/WalletBalanceCell";

function ProcessedRequestsTable({ currencySymbol }) {
  const {
    processedTransactions,
    loadingProcessed,
    handleViewUser,
    openDetailsModal,
    openPaymentDetailsDrawer,

    processedPageSize,
    setProcessedPageSize,
    processedCurrentPage,
    setProcessedCurrentPage,
    processedTotalCount,
  } = useWithdrawalReview();

  const getStatusColor = (status) => {
    switch (status) {
      case "declined":
      case "cancelled":
        return styles.red;
      case "refund":
        return styles.orange;
      default:
        return styles.green;
    }
  };

  const getStatusLabel = (status) =>
    status === "refund" ? AppStrings.WITHDRAWAL_REFUND_ACTION : status;

  const columns = [
    {
      title: AppStrings.REQUEST_DATE,
      dataIndex: "request_date_timestamp",
      key: "request_date_timestamp",
      width: 160,
      render: (text) => <span>{convertToUserTimezone(text)}</span>,
    },
    {
      title: AppStrings.USERNAME,
      dataIndex: "username",
      key: "username",
      width: 150,
      render: (text, record) => (
        <div className={styles.userImage}>
          <UserAvatar />
          <button onClick={() => handleViewUser(record)}>
            <span>{text}</span>
          </button>
        </div>
      ),
    },
    {
      title: AppStrings.TRANSACTION_ID,
      dataIndex: "transaction_id",
      key: "transaction_id",
      width: 250,
    },
    {
      title: AppStrings.LAST_DEPOSIT_AMOUNT,
      dataIndex: "last_deposit_amount",
      key: "last_deposit_amount",
      width: 140,
      render: (text, record) => (
        <div className={styles.lastDepositCol}>
          <span>
            {formatCurrency(Number(text ?? 0), currencySymbol, 2)}
          </span>
          <span className={styles.lastDepositColDate}>
            {formatTimestampOrDisplay(
              record.last_deposit_date_timestamp,
              record.last_deposit_date,
              "MMM DD, HH:mm",
            )}
          </span>
        </div>
      ),
    },
    {
      title: AppStrings.WITHDRAWAL_AMOUNT,
      dataIndex: "withdrawal_amount",
      key: "withdrawal_amount",
      width: 160,
      render: (text) => (
        <span>
          {formatCurrency(Number(text ?? 0), currencySymbol, 2)}
        </span>
      ),
    },
    {
      title: <WalletColumnTitle />,
      dataIndex: "cash_balance",
      key: "wallet",
      width: 200,
      render: (_text, record) => (
        <WalletBalanceCell
          balance={record.cash_balance}
          percentChange7d={record.cash_balance_7d_change_pct}
        />
      ),
    },
    {
      title: AppStrings.FTW,
      dataIndex: "is_first_time_withdrawal",
      key: "is_first_time_withdrawal",
      width: 70,
      align: "center",
      render: (isFirstTimeWithdrawal) => (
        isFirstTimeWithdrawal === 1 || isFirstTimeWithdrawal === "1" || isFirstTimeWithdrawal === true ? (
          <CheckCircleFilled style={{ color: "#52c41a", fontSize: 18 }} />
        ) : (
          <CloseCircleFilled style={{ color: "#ff4d4f", fontSize: 18 }} />
        )
      ),
    },
    {
      title: AppStrings.PAYMENT_GATEWAY,
      dataIndex: "payment_gateway",
      key: "payment_gateway",
      width: 120,
    },
    {
      title: AppStrings.DECISION,
      dataIndex: "transaction_status",
      key: "transaction_status",
      width: 140,
      render: (text) => (
        <span className={`${styles.status} ${getStatusColor(text)}`}>
          {getStatusLabel(text)}
        </span>
      ),
    },
    {
      title: AppStrings.PAM_REVIEWER,
      dataIndex: "reviewed_by",
      key: "reviewed_by",
      width: 150,
      render: (text) => (
        <span>{text || "-"}</span>
      )
    },
    {
      title: AppStrings.REVIEW_TIME,
      dataIndex: "review_start_timestamp",
      key: "review_start_timestamp",
      width: 150,
      render: (text) => (
        <span>{convertToUserTimezone(text)}</span>
      )
    },
    {
      title: AppStrings.TIME_OF_DECISION,
      dataIndex: "review_decision_timestamp",
      key: "review_decision_timestamp",
      width: 150,
      render: (text) => <span>{convertToUserTimezone(text)}</span>,
    },
    {
      title: AppStrings.STATE,
      dataIndex: "subregion",
      key: "subregion",
      width: 140,
      render: (_, record) => {
        const v = record?.subregion;
        if (typeof v !== "string" || v.trim() === "") {
          return <span style={{ color: "#6B7280" }}>—</span>;
        }
        return <span>{v}</span>;
      },
    },
    {
      title: AppStrings.METHOD,
      dataIndex: "method",
      key: "method",
      width: 160,
      render: (_, record) => {
        const v = record?.method ?? record?.withdrawal_method;
        if (typeof v !== "string" || v.trim() === "") {
          return <span style={{ color: "#6B7280" }}>—</span>;
        }
        return <span>{v}</span>;
      },
    },
    {
      title: AppStrings.ACTIONS,
      key: "actions",
      width: 80,
      fixed: "right",
      render: (_, record) => (
        <div className={styles.actionButtonGroup}>
          <button
            type="button"
            className={styles.actionButton}
            onClick={() => openDetailsModal(record)}
          >
            <EyeOutlined />
          </button>
          <button
            type="button"
            className={styles.actionButton}
            aria-label={AppStrings.PAYMENT_DETAILS}
            onClick={() => openPaymentDetailsDrawer(record)}
          >
            <CreditCardOutlined />
          </button>
        </div>
      ),
    },
  ];

  return (
    <>
      <DataTable
        loading={loadingProcessed}
        columns={columns}
        dataSource={processedTransactions?.transactions || []}
        pagination={false}
        scrollPreset="xWide"
        rowKey={(record) => record.transaction_id}
        className={styles.customTable}
      />
      <CustomPagination
        total={processedTotalCount || 0}
        pageSize={processedPageSize}
        currentPage={processedCurrentPage}
        onPageSizeChange={(value) => {
          setProcessedCurrentPage(1);
          setProcessedPageSize(value);
        }}
        onPageChange={(page) => setProcessedCurrentPage(page)}
      />
    </>
  );
}

export default ProcessedRequestsTable;
